import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useCreateScheduleEntry } from './useCreateScheduleEntry';
import { log as logger } from '../lib/logger';
import { useAuthStore } from '../stores/authStore';
import { localWallClockToInstant } from '../components/schedule/scheduleHelpers';

async function logActivity(businessId: string, type: string, desc: string, entityType: string, entityId: string, userId: string) {
  try { await supabase.from('activity_log').insert({ business_id: businessId, activity_type: type, description: desc, entity_type: entityType, entity_id: entityId, logged_by: userId }); } catch { /* non-critical */ }
}

interface CreateJobData {
  business_id: string;
  title: string;
  created_by: string;
  description?: string;
  client_name?: string;
  location?: string;
  address?: string;
  priority?: string;
  assigned_id?: string;
  assigned_type?: string;
  scheduled_date?: string;
  scheduled_time?: string;
  notes?: string;
}

export function useCreateJob() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createScheduleEntry = useCreateScheduleEntry();

  const createJob = useCallback(async (data: CreateJobData): Promise<boolean> => {
    setLoading(true); setError(null);
    try {
      if (!data.title.trim()) { setError('Title is required'); setLoading(false); return false; }

      // 1. Create work_order (source record)
      const { data: wo, error: woErr } = await supabase.from('work_orders').insert({
        business_id: data.business_id,
        title: data.title.trim(),
        status: 'new',
        created_by: data.created_by,
        description: data.description || null,
        client_name: data.client_name || null,
        location: data.location || null,
        address: data.address || null,
        priority: data.priority || 'medium',
        assigned_id: data.assigned_id || null,
        assigned_type: data.assigned_type || null,
        scheduled_date: data.scheduled_date || null,
        scheduled_time: data.scheduled_time || null,
        notes: data.notes || null,
      }).select('id').maybeSingle();
      if (woErr) throw woErr;

      await logActivity(data.business_id, 'created', `Job created: ${data.title}`, 'work_order', wo?.id ?? '', data.created_by);

      // 2. Create schedule_entry via canonical RPC.
      if (data.scheduled_date) {
        const startTime = data.scheduled_time || '09:00';
        const [h, m] = startTime.split(':').map(Number);
        const endH = h + 1;
        const endTime = `${String(endH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        // start_at/end_at are timestamptz — build true instants at the business tz
        // (A2 P2), not naive wall-clock-as-UTC strings.
        const tz = useAuthStore.getState().business?.timezone ?? null;
        const entryRes = await createScheduleEntry({
          type: 'job',
          title: data.title.trim(),
          startAt: localWallClockToInstant(data.scheduled_date, `${startTime}:00`, tz),
          endAt: localWallClockToInstant(data.scheduled_date, `${endTime}:00`, tz),
          assignedTo: data.assigned_id ? [data.assigned_id] : undefined,
          jobId: wo?.id ?? undefined,
          locationName: data.location || undefined,
          locationAddress: data.address || undefined,
          notes: data.notes || undefined,
          idempotencyKey: crypto.randomUUID(),
        });
        if (!entryRes.success) {
          logger.warn('Schedule entry creation failed for job:', wo?.id ?? '(no id)', entryRes.error.code);
        }
      }

      setLoading(false);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create job');
      setLoading(false);
      return false;
    }
  }, [createScheduleEntry]);

  return { createJob, loading, error };
}
